import { useState } from 'react';
import { Link } from 'react-router-dom';
import { date, money } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import {
  PROJECT_STATUS_LABELS,
  useDeleteWorkEntry,
  useDeliverables,
  useInvoiceWorkEntries,
  useProjects,
  useUnbilledSummary,
  useWorkEntries,
  WORK_CATEGORY_LABELS,
  type WorkEntry,
} from '../lib/projects-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * İŞLER ve İŞ KAYITLARI.
 *
 * ⚠⚠ "SEÇİLENLERİ FATURALA" TASLAK ÜRETİR. Düzenleme (numara tahsisi,
 * dondurma, Peppol) geri dönüşsüzdür ve fatura ekranından, insan onayıyla
 * yapılır.
 *
 * ⚠ HANGİ İŞLERİN FATURALANACAĞI AÇIKÇA SEÇİLİR. "Tümünü faturala" gibi bir
 * kestirme, henüz bitmemiş ya da müşteriyle konuşulmamış bir işi de belgeye
 * sokardı.
 */
export function ProjectsPage() {
  const projects = useProjects();
  const ozet = useUnbilledSummary();
  const [seciliProje, setSeciliProje] = useState<string>('');
  const [yalnizFaturasiz, setYalnizFaturasiz] = useState(true);
  const entries = useWorkEntries(seciliProje === '' ? null : seciliProje, yalnizFaturasiz);
  const deliverables = useDeliverables(seciliProje === '' ? null : seciliProje);
  const faturala = useInvoiceWorkEntries();
  const sil = useDeleteWorkEntry();

  const [secili, setSecili] = useState<Set<string>>(new Set());
  const [hata, setHata] = useState<string | null>(null);
  const [uretilen, setUretilen] = useState<{ id: string; count: number } | null>(null);

  const satirlar = entries.data ?? [];
  const seciliListe = satirlar.filter((e) => secili.has(e.id));

  function toggle(id: string) {
    setSecili((önce) => {
      const yeni = new Set(önce);
      if (yeni.has(id)) yeni.delete(id);
      else yeni.add(id);
      return yeni;
    });
  }

  return (
    <>
      <PageHeader
        title="İşler"
        description="Yapılan işlerin kaydı ve faturaya dönüştürülmesi. Dönüştürmek, düzenlemek değildir."
      />

      {/*
        ⚠ SAYAÇ BİR TELAFİDİR: iş kaydetmekle faturalamak ayrı eylemler olduğu
        için unutulan işler sessizce birikebilir; görünür olmaları o riskin
        karşılığı.
      */}
      {(ozet.data?.entries ?? 0) > 0 && (
        <Card className="mt-4">
          <div className="px-4 py-3 text-sm">
            <strong>{ozet.data?.entries}</strong> faturalanmamış iş kaydı var
            {ozet.data?.oldestDate ? (
              <>
                {' '}
                — en eskisi <strong>{date(ozet.data.oldestDate)}</strong> tarihli.
              </>
            ) : (
              '.'
            )}
          </div>
        </Card>
      )}

      <Card className="mt-4">
        <CardHeader title="Projeler" />
        {projects.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : projects.isError ? (
          <ErrorState message={errorMessage(projects.error)} />
        ) : (projects.data ?? []).length === 0 ? (
          <EmptyState title="Henüz proje yok" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Kod</Th>
                <Th>Ad</Th>
                <Th>Durum</Th>
                <Th align="right">Saatlik</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {(projects.data ?? []).map((p) => (
                <tr key={p.id}>
                  <Td className="font-mono text-xs">{p.code}</Td>
                  <Td>{p.name}</Td>
                  <Td>
                    <Badge tone={p.status === 'ACTIVE' ? 'ok' : 'neutral'}>
                      {PROJECT_STATUS_LABELS[p.status]}
                    </Badge>
                  </Td>
                  <Td align="right">{p.hourlyRate ? money(p.hourlyRate) : '—'}</Td>
                  <Td>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setSeciliProje(p.id);
                        setSecili(new Set());
                        setUretilen(null);
                      }}
                    >
                      İş kayıtları
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {seciliProje !== '' && (
        <>
          <Card className="mt-4">
            <CardHeader
              title="İş kayıtları"
              description="Faturalanacakları seçin. Her iş kaydı faturada KENDİ satırını alır."
            />
            <div className="flex items-center gap-3 border-b border-[var(--c-border)] px-4 py-3">
              <Checkbox
                label="Yalnız faturalanmamışlar"
                checked={yalnizFaturasiz}
                onChange={(e) => {
                  setYalnizFaturasiz(e.target.checked);
                  setSecili(new Set());
                }}
              />
              <Select
                label=""
                value={seciliProje}
                onChange={(e) => {
                  setSeciliProje(e.target.value);
                  setSecili(new Set());
                }}
              >
                {(projects.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} — {p.name}
                  </option>
                ))}
              </Select>
            </div>

            {entries.isPending ? (
              <div className="px-4 py-6">
                <Spinner />
              </div>
            ) : satirlar.length === 0 ? (
              <EmptyState title="Bu projede iş kaydı yok" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th> </Th>
                    <Th>Tarih</Th>
                    <Th>Açıklama</Th>
                    <Th align="right">Saat</Th>
                    <Th align="right">Oran</Th>
                    <Th>Durum</Th>
                    <Th> </Th>
                  </tr>
                </thead>
                <tbody>
                  {satirlar.map((e) => (
                    <tr key={e.id}>
                      <Td>
                        {/*
                          ⚠ FATURALANMIŞ ya da FATURALANAMAZ iş seçilemez:
                          birincisi çift faturalama, ikincisi garanti işini
                          müşteriye yansıtmak olurdu.
                        */}
                        <input
                          type="checkbox"
                          checked={secili.has(e.id)}
                          disabled={e.billedInvoiceId !== null || !e.billable}
                          onChange={() => toggle(e.id)}
                        />
                      </Td>
                      <Td className="text-xs">{date(e.workDate)}</Td>
                      <Td>
                        <span className="text-sm">{e.description}</span>
                        <br />
                        <span className="text-xs text-[var(--c-ink-soft)]">
                          {WORK_CATEGORY_LABELS[e.category]}
                        </span>
                      </Td>
                      <Td align="right">{e.hours}</Td>
                      <Td align="right">{money(e.rate)}</Td>
                      <Td>{durumRozeti(e)}</Td>
                      <Td>
                        {e.billedInvoiceId === null && (
                          <Button
                            variant="ghost"
                            disabled={sil.isPending}
                            onClick={() => {
                              setHata(null);
                              sil.mutate(e.id, {
                                onError: (err: unknown) => setHata(errorMessage(err)),
                              });
                            }}
                          >
                            Sil
                          </Button>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <div className="space-y-2 border-t border-[var(--c-border)] px-4 py-3">
              <p className="text-xs text-[var(--c-ink-soft)]">
                Seçilen {seciliListe.length} kayıt ·{' '}
                {seciliListe.reduce((a, e) => a + Number(e.hours), 0).toFixed(2)} saat
              </p>
              <div className="flex items-center gap-2">
                <Button
                  disabled={seciliListe.length === 0 || faturala.isPending}
                  onClick={() => {
                    setHata(null);
                    setUretilen(null);
                    faturala.mutate(
                      { projectId: seciliProje, entryIds: seciliListe.map((e) => e.id) },
                      {
                        onSuccess: (r) => {
                          setUretilen({ id: r.invoiceId, count: r.entryCount });
                          setSecili(new Set());
                        },
                        onError: (err: unknown) => setHata(errorMessage(err)),
                      },
                    );
                  }}
                >
                  Seçilenlerden TASLAK fatura üret
                </Button>
                {faturala.isPending && <Spinner />}
              </div>
              {uretilen && (
                <p className="text-sm">
                  {uretilen.count} kayıttan taslak üretildi.{' '}
                  <Link className="underline" to={`/invoices/${uretilen.id}`}>
                    Faturayı aç
                  </Link>{' '}
                  — <strong>henüz düzenlenmedi</strong>.
                </p>
              )}
              {hata && <p className="text-xs text-[var(--c-danger)]">{hata}</p>}
            </div>
          </Card>

          <Card className="mt-4">
            <CardHeader
              title="Teslimatlar"
              description="Yayına alınan siteler, kurulan kiosklar. Mali bir belge değil — bir yıl sonra gelen destek talebinde tek kayıt kaynağı."
            />
            {(deliverables.data ?? []).length === 0 ? (
              <EmptyState title="Teslimat kaydı yok" />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Ad</Th>
                    <Th>Tür</Th>
                    <Th>Adres</Th>
                    <Th>Yayın</Th>
                  </tr>
                </thead>
                <tbody>
                  {(deliverables.data ?? []).map((d) => (
                    <tr key={d.id}>
                      <Td>{d.name}</Td>
                      <Td className="text-xs">{d.type}</Td>
                      <Td className="text-xs">{d.url ?? '—'}</Td>
                      <Td className="text-xs">{d.wentLiveAt ? date(d.wentLiveAt) : '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      )}
    </>
  );
}

/**
 * ⚠ ÜÇ DURUM AYRI GÖRÜNÜR. "Faturalanmadı" ile "faturalanamaz" aynı rozetle
 * gösterilseydi, garanti işi unutulmuş bir fatura gibi okunurdu.
 */
function durumRozeti(e: WorkEntry) {
  if (e.billedInvoiceId !== null) return <Badge tone="ok">Faturalandı</Badge>;
  if (!e.billable) return <Badge tone="neutral">Faturalanamaz</Badge>;
  return <Badge tone="warn">Faturalanmadı</Badge>;
}
